<template>
    <TransitionRoot as="template" :show="open" class="">
        <Dialog as="div" class="relative z-10" @close="handleClose">
            <div class="fixed inset-0" />

            <div class="fixed inset-0 overflow-hidden">
                <div class="absolute inset-0 overflow-hidden">
                    <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0">
                        <TransitionChild
                            as="template"
                            enter="transform transition ease-in-out duration-500 sm:duration-700"
                            enter-from="translate-x-full"
                            enter-to="translate-x-0"
                            leave="transform transition ease-in-out duration-500 sm:duration-700"
                            leave-from="translate-x-0"
                            leave-to="translate-x-full"
                        >
                            <DialogPanel class="pointer-events-auto w-screen">
                                <form @submit.prevent="$emit('submit')" class="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                                    <div class="h-0 flex-1 overflow-y-auto">
                                        <div class="sticky top-0 z-10 bg-primary-600 py-3 pl-6 pr-4 sm:pl-8 sm:pr-6">
                                            <div class="flex items-center justify-between">
                                                <DialogTitle class="text-base font-semibold leading-6 text-white">{{ title }}</DialogTitle>
                                                <div class="ml-3 flex h-7 items-center">
                                                    <button
                                                        type="button"
                                                        class="relative rounded-md bg-primary-700 text-primary-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="handleClose"
                                                        :disabled="isTransitioning"
                                                    >
                                                        <span class="absolute -inset-2.5" />
                                                        <span class="sr-only">Close panel</span>
                                                        <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex flex-1 flex-col justify-between">
                                            <slot></slot>
                                        </div>
                                    </div>
                                    <div class="flex flex-shrink-0 justify-end px-4 py-4">
                                        <slot name="buttons"></slot>
                                    </div>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { ref, watch } from 'vue'

const props = defineProps<{
    title: string
    open: Boolean
}>()

const emit = defineEmits(['submit', 'close'])

const isTransitioning = ref(false)
let closeTimeout: ReturnType<typeof setTimeout> | null = null

// Watch for open prop to manage transition state
watch(
    () => props.open,
    (newVal, oldVal) => {
        if (newVal !== oldVal) {
            isTransitioning.value = true
            if (closeTimeout) clearTimeout(closeTimeout)
            // Transition duration matches the one in TransitionChild (700ms max)
            closeTimeout = setTimeout(() => {
                isTransitioning.value = false
            }, 750)
        }
    }
)

function handleClose() {
    if (isTransitioning.value) return // Block close during transition
    emit('close')
}
</script>
